import { useState, useEffect, useCallback } from 'react';

import { 
  getIPLocation, 
  IPLocationData, 
  IPGeolocationResult,
  formatLocationAsAddress,
  isLocationAccurate,
  getLocationConfidence,
  refreshIPLocation,
  getIPLocationCacheStats,
  isIPLocationCacheAvailable
} from '@/utils/ipGeolocation';

interface UseIPLocationState {
  data: IPLocationData | null;
  isLoading: boolean;
  error: string | null;
  isVPN: boolean;
  confidence: number;
  isAccurate: boolean;
  addressString: string;
  isCached: boolean;
  cacheAvailable: boolean;
}

interface UseIPLocationReturn extends UseIPLocationState {
  refetch: () => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
  getCacheStats: () => Promise<{
    totalEntries: number;
    currentIPCached: boolean;
    oldestEntry: Date | null;
    newestEntry: Date | null;
    cacheHitRate?: number;
  }>;
}

/**
 * Hook for detecting user location based on IP address
 * Automatically fetches location on mount unless disabled
 */
export const useIPLocation = (options?: {
  autoFetch?: boolean;
  onSuccess?: (data: IPLocationData) => void;
  onError?: (error: string) => void;
}): UseIPLocationReturn => {
  const { autoFetch = true, onSuccess, onError } = options || {};

  const [state, setState] = useState<UseIPLocationState>({
    data: null,
    isLoading: false,
    error: null,
    isVPN: false,
    confidence: 0,
    isAccurate: false,
    addressString: '',
    isCached: false,
    cacheAvailable: isIPLocationCacheAvailable(),
  });

  const processLocationResult = useCallback((result: IPGeolocationResult) => {
    if (result.success && result.data) {
      const confidence = getLocationConfidence(result);
      const accurate = isLocationAccurate(result);
      const addressString = formatLocationAsAddress(result.data);

      setState(prev => ({
        ...prev,
        data: result.data || null,
        isLoading: false,
        error: null,
        isVPN: result.isVPN || false,
        confidence,
        isAccurate: accurate,
        addressString,
        isCached: result.cached || false,
      }));

      onSuccess?.(result.data);
    } else {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: result.error || 'Failed to get location',
        confidence: 0,
        isAccurate: false,
        isCached: false,
      }));

      onError?.(result.error || 'Failed to get location');
    }
  }, [onSuccess, onError]);

  const fetchLocation = useCallback(async () => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const result = await getIPLocation();
      processLocationResult(result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        confidence: 0,
        isAccurate: false,
      }));

      onError?.(errorMessage);
    }
  }, [processLocationResult, onError]);

  const refreshLocation = useCallback(async () => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const result = await refreshIPLocation();
      processLocationResult(result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        confidence: 0,
        isAccurate: false,
        isCached: false,
      }));

      onError?.(errorMessage);
    }
  }, [processLocationResult, onError]);

  const clearError = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: null,
    }));
  }, []);

  const getCacheStats = useCallback(async () => getIPLocationCacheStats(), []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch && !state.data && !state.isLoading) {
      fetchLocation();
    }
  }, [autoFetch, state.data, state.isLoading, fetchLocation]);

  return {
    ...state,
    refetch: fetchLocation,
    refresh: refreshLocation,
    clearError,
    getCacheStats,
  };
};

/**
 * Hook for getting IP location with manual trigger only
 * Useful when you want to control exactly when to fetch location
 */
export const useIPLocationLazy = (options?: {
  onSuccess?: (data: IPLocationData) => void;
  onError?: (error: string) => void;
}) => useIPLocation({ ...options, autoFetch: false });